import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ReactNode,
} from "react"
import { DEFAULT_LANGUAGE } from "../api/config"
import { local, StorageKey } from "./storage"

/**
 * Язык контента — тот, что выбран в шапке.
 *
 * Он нужен почти каждому экрану и входит в ключ почти каждого запроса. Раньше
 * экраны читали его прямо из хранилища при рендере, а шапка держала свою копию
 * в состоянии — две правды об одном значении. Теперь значение одно и живёт
 * здесь; шапка его меняет, экраны только читают.
 *
 * Смена языка пересобирает содержимое (`<Outlet key={languageId}>` в `Layout`):
 * названия, описания и переводы приходят с сервера уже на выбранном языке,
 * и черновики от прошлого языка к новому не относятся.
 */
interface LanguageValue {
	languageId: number
	setLanguageId: (id: number) => void
}

const LanguageContext = createContext<LanguageValue | null>(null)

export function LanguageProvider({ children }: { children: ReactNode }) {
	const [languageId, setStored] = useState(
		() => local.number(StorageKey.language) ?? DEFAULT_LANGUAGE
	)

	const setLanguageId = useCallback((id: number) => {
		setStored(id)
		local.write(StorageKey.language, id)
	}, [])

	const value = useMemo(
		() => ({ languageId, setLanguageId }),
		[languageId, setLanguageId]
	)

	return (
		<LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
	)
}

function useLanguage(): LanguageValue {
	const value = useContext(LanguageContext)
	if (!value) throw new Error("useLanguage вызван вне LanguageProvider")
	return value
}

/** Язык контента для запросов экрана. */
export function useLanguageId(): number {
	return useLanguage().languageId
}

/** Только для шапки: переключение языка. */
export function useLanguageSwitch(): LanguageValue {
	return useLanguage()
}
